import format from 'date-fns/format';
import isWithinInterval from 'date-fns/isWithinInterval';
import endOfDay from 'date-fns/endOfDay';
import startOfDay from 'date-fns/startOfDay';
import isValid from 'date-fns/isValid';

import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import differenceInMonths from 'date-fns/differenceInMonths';
import { type Locale } from 'date-fns';

import { type AutoCompleteOption } from '../types';

import { getNumbersArray } from './mathUtils';
import { isProd } from '../config';
/*======================= Types =======================*/

/**Represents valid parameters accepted by the native JavaScript `Date` constructor.
 * Useful for functions that pass arguments directly to `new Date(...)`.*/
export type NativeDateType = ConstructorParameters<typeof Date>[number];

/** Represents a basic date input value. Commonly used for standard date pickers, API payloads, or display formats.*/
export type DateType = Date | string | null | undefined;
export type FullDateType = DateType | NativeDateType | number | unknown;

/*======================= Constants =======================*/
/** 1 second in milliseconds (1,000 ms) */
export const ONE_SECOND_IN_MS = 1_000;

/** 1 minute (60 seconds) in milliseconds (60 * 1,000 = 60,000 ms) */
export const ONE_MINUTE_IN_MS = 60 * ONE_SECOND_IN_MS;

/** 1 hour (60 minutes) in milliseconds (60 * 60 * 1,000 = 3_600_000 ms) */
export const ONE_HOUR_IN_MS = 60 * ONE_MINUTE_IN_MS;

/** 1 day (24 hours) in milliseconds (24 * 60 * 60 * 1,000 = 86_400_000 ms) */
export const ONE_DAY_IN_MS = 24 * ONE_HOUR_IN_MS;

/** 1 week (7 days) in milliseconds (7 * 24 * 60 * 60 * 1,000 = 604_800_000 ms) */
export const ONE_WEEK_IN_MS = 7 * ONE_DAY_IN_MS;
/** 12 =Months in a year. */
export const MONTH_NUM_IN_YEAR = 12;
/*======================= Functions  =======================*/
export const creditCardExpirationYearOptions: AutoCompleteOption[] = getNumbersArray(
    2020,
    new Date().getFullYear() + MONTH_NUM_IN_YEAR
).map((v) => ({ _id: v, name: `${v}` }));

export const creditCardExpirationMonthOptions = getNumbersArray(0, MONTH_NUM_IN_YEAR).map(
    (monthCode: number): AutoCompleteOption => ({
        _id: monthCode,
        name: `${monthCode + 1 <= 9 ? '0' : ''}${monthCode + 1}`
    })
);

// 18.01.2020 ---> 18/01/2020
export const getDateString = (date: DateType) => {
    return getDateByFormat(date, 'dd/MM/yyyy');
};
const _timeOrderFormat = (string: string) => {
    return string
        .split(':')
        .map((str: string) => (str.length > 1 ? str : `0${str}`))
        .join(':');
};
// 9:12:5 ---> 09:12:05
export const getTimeString = (date: DateType) => {
    if (!date) return '';

    const formatDate = new Date(date).toLocaleTimeString();
    return _timeOrderFormat(formatDate);
};

// dd/MM/yyyy HH:mm
export const getDateTimeString = (date: DateType, dateFormat: string = 'dd/MM/yyyy HH:mm') => {
    return getDateByFormat(date, dateFormat || 'dd/MM/yyyy HH:mm');
};

export const getDateByFormat = (date: DateType, dateFormat: string, noDateText?: any): string => {
    if (!date) return noDateText || '';

    return format(new Date(date), dateFormat);
};
/**
 * Checks whether a date falls within the given date range (inclusive).
 * The comparison uses the full days of the start and end dates,
 * meaning the interval is from the start of `startDate`'s day
 * to the end of `endDate`'s day.
 *
 * @param {NativeDateType} date - The date to check.
 * @param {NativeDateType} startDate - The start of the date range.
 * @param {NativeDateType} endDate - The end of the date range.
 * @returns {boolean} `true` if the date is within the inclusive range; otherwise, `false`.
 */
export const isDateBetweenDates = (
    date: NativeDateType,
    startDate: NativeDateType,
    endDate: NativeDateType
) => {
    if (!date || !endDate || !startDate) {
        return false;
    }
    const start = startOfDay(new Date(startDate));
    const end = endOfDay(new Date(endDate));
    return isWithinInterval(new Date(date), { start, end });
};
/**
 * Creates a new date based on `baseDate` and copies the hours and minutes
 * from `date`. If `date` is not provided, the time is set to 00:00.
 *
 * @param baseDate - The date whose year, month, and day are preserved.
 * @param date - The date from which to copy the hours and minutes.
 * @returns A new `Date` with the date portion from `baseDate` and the time
 * from `date`, or `null` if `baseDate` is not provided.
 */
export const setHoursMinutesToDateByDate = (baseDate: DateType, date: DateType) => {
    if (!baseDate) return null;

    const updatedDate = new Date(baseDate);
    updatedDate.setMinutes(date ? new Date(date)?.getMinutes() || 0 : 0);
    updatedDate.setHours(date ? new Date(date)?.getHours() || 0 : 0);

    return updatedDate;
};
export const isDateValid = (date: NativeDateType | DateType) => {
    if (!date || `${new Date(date)}` === 'Invalid Date') return false;
    return true;
};

export const getDurationDays = (startDate: DateType, endDate: DateType): number | null => {
    if (!startDate || !endDate || !isDateValid(startDate) || !isDateValid(endDate)) {
        return null;
    }
    return differenceInCalendarDays(new Date(endDate), new Date(startDate)) + 1;
};
/**
 * Nights between two dates — one fewer than the inclusive day count, so
 * 15-Mar → 19-Mar is 4 nights, matching TB's hotel form.
 *
 * This used to be written inline as `getDurationDays(a, b) ?? 1 - 1` in seven
 * places. `??` binds looser than `-`, so that parsed as `?? (1 - 1)` and the
 * subtraction never ran on the path that has dates — every "Nights" field was
 * showing the day count instead.
 */
export const getDurationNights = (startDate: DateType, endDate: DateType): number => {
    return (getDurationDays(startDate, endDate) ?? 1) - 1;
};
/**
 * Converts any valid date to an ISO 8601 string (e.g., "2026-07-14T12:34:09.562Z").
 * Returns a fallback string (default is empty string) if the date is invalid or missing.
 * the format is "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"
 */
export const getFormatISOString = (date: DateType, fallback: string = ''): string => {
    try {
        if (!date || !isDateValid(date)) return fallback;

        return new Date(date).toISOString();
    } catch (error) {
        if (!isProd) console.error('[Date Utils] - ERROR - failed to format date as ISO string:', error);
        return '';
    }
};
export const getFormatDateYYYYMMDD = (date: DateType, fallback: string = ''): string => {
    try {
        if (!date) return fallback;
        const parsedDate = new Date(date);
        if (!isValid(parsedDate)) return fallback;
        return format(parsedDate, 'yyyy/MM/dd');
    } catch (error) {
        if (!isProd) {
            console.error('[Date Utils] - ERROR - failed to format date string:', error);
        }
        return fallback;
    }
};
export const formatTimestamp = (
    timestamp?: DateType | NativeDateType,
    dateFormat: string = 'dd/MM/yyyy HH:mm'
): string => {
    if (!timestamp) return 'N/A';

    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'N/A';

    return format(date, dateFormat || 'dd/MM/yyyy HH:mm');
};
// Helper: safely convert anything to timestamp (ms), return NaN if invalid
export const toTimestamp = (input: DateType | NativeDateType, defaultValue: number = NaN): number => {
    if (input == null || input === '') return defaultValue;
    const date = new Date(input);
    return date.getTime(); // getTime() returns NaN automatically if invalid
};
export const calculateDaysBetweenDates = (date1: DateType, date2: DateType): number => {
    // Handle null/undefined/empty
    if (!date1 || !date2) return 0;
    const d1 = toTimestamp(date1);
    const d2 = toTimestamp(date2);

    // Check if dates are valid
    if (isNaN(d1) || isNaN(d2)) return 0;

    // Time difference in milliseconds
    const diffInMs = Math.abs(d2 - d1);
    // Convert to full days (round down – standard behavior)
    const days = Math.floor(diffInMs / (1000 * 60 * 60 * 24)); // 86400000 = 1000*60*60*24

    return days;
};

/**
 * Whole calendar months from `today` to `targetDate` — plain year/month
 * arithmetic (`(yearT-yearN)*12 + (monthT-monthN)`), NOT sensitive to the
 * day-of-month. "15 Sep -> 1 Jan" and "30 Sep -> 1 Jan" both count as 4.
 * Negative when `targetDate` is in the past; 0 when either date is invalid.
 *
 * This is the basis for Pelecard's installment-count business rule (see
 * `src/api/pelecardUtils/pelecardInstallments.ts` / `calculateMaxPayments`) — kept here,
 * generic and side-effect-free, so the date math and the payment policy
 * built on it can be tested independently.
 */
export const getMonthsUntil = (targetDate: DateType, today: DateType = new Date()): number => {
    if (!isDateValid(targetDate) || !isDateValid(today)) return 0;
    const target = new Date(targetDate as NativeDateType);
    const from = new Date(today as NativeDateType);
    return (
        (target.getFullYear() - from.getFullYear()) * MONTH_NUM_IN_YEAR +
        (target.getMonth() - from.getMonth())
    );
};

/**
 * Full months elapsed from `date` to `today` (date-fns `differenceInMonths`).
 * Unlike `getMonthsUntil` above, this IS sensitive to the day-of-month — a
 * month only counts once it has fully passed, so "15 Jan -> 14 Feb" is 0.
 * Used for age calculation (see `calcAgeByBirthDate` in `dataUtils.ts`).
 */
export const getFullMonthsSince = (date: NativeDateType, today: NativeDateType = new Date()): number => {
    return differenceInMonths(new Date(today), new Date(date));
};

/**
 * Whole calendar days from `today` to `targetDate` — signed (negative when
 * `targetDate` is in the past), compared at LOCAL MIDNIGHT for both dates
 * rather than a raw ms/86_400_000 divide, so a DST transition inside the
 * span can't shift the result onto the wrong calendar day. 0 when either
 * date is invalid.
 *
 * Distinct from `calculateDaysBetweenDates` above, which is unsigned
 * (absolute value) and ms-based (not DST-safe) — this one exists for gates
 * like "is the trip at least N days out", where sign and a DST-safe day
 * boundary both matter. Used by Pelecard's 60-day installment-split gate
 * (see `src/api/pelecardUtils/pelecardInstallments.ts` / `isSplitAllowed`).
 */
export const getCalendarDaysUntil = (targetDate: DateType, today: DateType = new Date()): number => {
    if (!isDateValid(targetDate) || !isDateValid(today)) return 0;
    const target = new Date(targetDate as NativeDateType);
    const from = new Date(today as NativeDateType);
    const targetMidnight = new Date(target.getFullYear(), target.getMonth(), target.getDate()).getTime();
    const fromMidnight = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
    return Math.round((targetMidnight - fromMidnight) / ONE_DAY_IN_MS);
};

export type DateRangeUnit = 'months' | 'weeks' | 'days';

/**
 * Adds or subtracts a time period from a date based on the unit type.
 */
const adjustDate = (date: NativeDateType, amount: number, unit: DateRangeUnit): Date => {
    const newDate = new Date(date);
    switch (unit) {
        case 'months':
            newDate.setMonth(newDate.getMonth() + amount);
            break;
        case 'weeks':
            newDate.setDate(newDate.getDate() + amount * 7);
            break;
        case 'days':
            newDate.setDate(newDate.getDate() + amount);
            break;
    }
    return newDate;
};

/**
 * Creates handlers for date range fields that auto-calculate the paired date
 * based on a configurable range limit.
 *
 * @param amount - Number of units for the date range (e.g., 12 for 12 months)
 * @param unit - The unit type: 'months', 'weeks', or 'days'
 * @param setFieldValue - Formik's setFieldValue function
 * @param fromFieldName - Name of the "from" date field
 * @param toFieldName - Name of the "to" date field
 */
export const createDateRangeHandlers = (
    amount: number,
    unit: DateRangeUnit,
    setFieldValue: (field: string, value: any) => void,
    fromFieldName: string,
    toFieldName: string
) => {
    const handleFromDateChange = (date: any) => {
        setFieldValue(fromFieldName, date);
        if (date && !isNaN(new Date(date).getTime())) {
            const newDate = adjustDate(new Date(date), amount, unit);
            if (newDate > new Date()) {
                setFieldValue(toFieldName, new Date());
            } else {
                setFieldValue(toFieldName, newDate);
            }
        }
    };

    const handleToDateChange = (date: any) => {
        setFieldValue(toFieldName, date);
        if (date && !isNaN(new Date(date).getTime())) {
            const newDate = adjustDate(new Date(date), -amount, unit);
            setFieldValue(fromFieldName, newDate);
        }
    };

    return { handleFromDateChange, handleToDateChange };
};

/**
 * Returns the initial date range for filter forms.
 * Creates a range from (today - range) to today.
 *
 * @param amount - Number of units to go back from today
 * @param unit - The unit type: 'months', 'weeks', or 'days'
 */
export const getInitialDateRange = (amount: number, unit: DateRangeUnit = 'months') => {
    const toDate = new Date();
    const fromDate = adjustDate(new Date(), -amount, unit);
    return { fromDate, toDate };
};
export const getFormattedDate = (date: DateType, locale: Locale, dateFormat: string = 'MMM d, y'): string => {
    if (!date || !isDateValid(date)) return '';

    return format(new Date(date), dateFormat, { locale });
};
/**
 * `en-GB` "day Mon year" display date (e.g. "1 Jan 2026" / "01 Jan 26"),
 * used on receipts, vouchers/PDFs and travel-file UI labels. Tolerant of
 * missing/invalid input — returns '' rather than throwing or rendering
 * "Invalid Date". `day`/`year` default to `'numeric'`; pass `'2-digit'` to
 * match a call site that displayed the shorter form before this was shared.
 */
export function formatShortDate(
    raw: FullDateType,
    options: { day?: 'numeric' | '2-digit'; year?: 'numeric' | '2-digit' } = {}
): string {
    if (!raw) return '';
    try {
        const d = raw instanceof Date ? raw : new Date(raw as NativeDateType);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleDateString('en-GB', {
            day: options.day ?? 'numeric',
            month: 'short',
            year: options.year ?? 'numeric'
        });
    } catch {
        return '';
    }
}
export const formatNumber = (value: number, locales: Intl.LocalesArgument = 'en-US') =>
    Number(value || 0).toLocaleString(locales, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/**
 * Returns whether the given date is in the future.
 *
 * If the provided date is invalid, the function returns `false`.
 *
 * @param date - The date to check.
 * @returns -{boolean} `true` if the date is valid and later than the current time; otherwise `false`.
 */
export const isFutureDate = (date: DateType | NativeDateType): boolean => {
    if (!isDateValid(date)) return false;
    return new Date(date as NativeDateType) > new Date();
};

/*==========isExpiredDate=========  */

/**
 * Checks whether a timestamp is older than the given TTL (time-to-live).
 *
 * @param {DateType | NativeDateType} updatedAt - The date/timestamp the value was last set, or `null`/`undefined` if never set.
 * @param {number} ttlMs - The maximum age (ms) before the value is considered expired.
 * @returns {boolean} `true` if `updatedAt` is missing/invalid or older than `ttlMs`, otherwise `false`.
 *
 * @example
 * isExpiredDate(cachedSession.updatedAt, ONE_DAY_IN_MS); // true if the session is missing or older than 1 day
 */
export function isExpiredDate(updatedAt: DateType | NativeDateType, ttlMs: number): boolean {
    const updatedAtMs = toTimestamp(updatedAt);
    if (isNaN(updatedAtMs)) return true;

    return isExpiredTimestamp(toTimestamp(updatedAt), ttlMs);
}
/**
 * Checks whether a timestamp (in milliseconds) is older than the given TTL.
 *
 * @param {number} timestampMs - Unix timestamp in milliseconds.
 * @param {number} ttlMs - The maximum allowed age in milliseconds.
 * @returns {boolean} `true` if the timestamp is invalid or older than `ttlMs`; otherwise `false`.
 *
 * @example
 * isExpiredTimestamp(Date.now() - 60_000, 30_000); // true
 */
export function isExpiredTimestamp(timestampMs: number, ttlMs: number): boolean {
    if (isNaN(timestampMs)) return true;

    return Date.now() - timestampMs >= ttlMs;
}
